import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { Line } from 'react-chartjs-2';
import 'chart.js/auto';   // 스케일·요소 일괄 등록 (부수효과 import)
import 'chartjs-adapter-date-fns';
import { ko } from 'date-fns/locale';
import { ChevronLeft, X } from 'lucide-react';
import NavigationBar from '../components/NavigationBar';
import PatientTabs from '../components/PatientTabs';
import usePatientSummary from '../utils/usePatientSummary';
import { API_BASE_URL } from '../config/config';
import SleepCard from '../components/SleepCard';
import { summarizeSleep } from '../utils/sleepSummary';
import tokens from '../design/tokens.json';
import '../css/VitalsDetail.css';
import {
    SERIES_METRICS, MAX_SERIES_DAYS, MAX_DAILY_DAYS,
    daysAgo, toDateInput, dayCount, describeVitalsError,
    fetchVitalsSeries, fetchVitalsDaily, fetchVitalsLatestDate
} from '../utils/vitalsApi';

// 스마트링 바이탈 상세.
//
// 별도 페이지로 둔 이유: DashboardDetail 은 이미 2,000줄이 넘고 탭 로직이 기존
// 4개 지표의 안전범위·임계값과 얽혀 있다. 스마트링 지표는 단위도 척도도 달라서
// 그 탭 줄에 끼우면 조건문만 늘어난다.
//
// 기능이 꺼져 있으면(SMARTRING_VITALS_ENABLED=false) 서버가 503 VITALS_DISABLED
// 를 준다. 그때는 화면이 왜 비었는지 설명한다 — 빈 차트만 보여주면 데이터가
// 없는 것인지 기능이 꺼진 것인지 구분할 수 없다.

/* 차트 색은 토큰에서만 온다(design.md 1절). tokens.color.chart 를 그냥 chart 로 풀지
 * 않는 것은 Chart.js 콜백이 인자 이름으로 chart 를 쓰기 때문이다 — 가려진다. */
const chartColor = tokens.color.chart;
const textColor = tokens.color.text;

/* 지표별 선 색.
 *
 * vitalsApi.SERIES_METRICS 의 color 는 토큰 이전의 옛 팔레트라 화면에서
 * 쓰지 않는다. 그 상수 자체는 이번 화면 작업의 대상이 아니어서 손대지 않고,
 * 여기서 지표 키 → 토큰 색으로만 갈아탄다. 색상환 위치는 그대로 옮겼다 —
 * 심박은 붉게, HRV 는 푸르게 보던 눈을 바꾸지 않기 위해서다.
 * 키가 늘어나면 chart-6(중립 회색)으로 떨어진다. */
const METRIC_LINE_COLOR = {
    heartRate: chartColor['4'],
    hrv: chartColor['1'],
    stress: chartColor['3'],
    temperature: chartColor['5'],
    spo2: chartColor['2']
};

// 동기화 시각. 오늘이면 시:분만, 아니면 날짜까지 보여준다 —
// 대부분 오늘이라 날짜를 늘 붙이면 읽는 눈만 늘어난다.
const formatSyncTime = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    const time = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
    const isToday = date.toDateString() === new Date().toDateString();
    if (isToday) return `오늘 ${time}`;
    return `${date.getMonth() + 1}/${date.getDate()} ${time}`;
};

const formatNumber = (value, digits = 1) => (
    value === null || value === undefined ? '-' : Number(value).toFixed(digits)
);

const formatMinutes = (minutes) => {
    if (minutes === null || minutes === undefined) return '-';
    const hours = Math.floor(minutes / 60);
    return `${hours}시간 ${Math.round(minutes % 60)}분`;
};

const formatSeconds = (seconds) => (
    seconds === null || seconds === undefined ? '-' : formatMinutes(seconds / 60)
);

// 배터리 잔량 등급. 낮을수록 눈에 띄게 한다 — 링이 꺼져 있으면 그 뒤로 들어오는
// 데이터가 아예 없으므로, 데이터가 비어 보이는 이유를 여기서 먼저 알 수 있다.
// 경계값(20·50)은 그대로다. 자기 색을 갖고 있던 것을 공용 배지 단계로만 옮겼다.
// 충전 중이면 잔량이 낮아도 곧 회복된다 — 경고 단계를 덮어쓴다.
const batteryBadgeClass = (battery) => {
    if (battery.charging) return 'sds-badge--normal';
    const percent = battery.percent;
    if (percent === null || percent === undefined) return 'sds-badge--none';
    if (percent <= 20) return 'sds-badge--danger';
    if (percent <= 50) return 'sds-badge--caution';
    return 'sds-badge--none';
};

// 툴팁. 배터리 값은 마지막 동기화가 아니라 "배터리를 알려준 마지막 동기화" 시각의
// 것이라 며칠 지난 값일 수 있다. 그 시각을 반드시 같이 보여준다.
const describeBattery = (battery) => {
    const parts = [];
    if (battery.reportedAt) parts.push(`${formatSyncTime(battery.reportedAt)} 기준`);
    if (battery.deviceId) parts.push(`기기 ${battery.deviceId}`);
    if (battery.charging === null) parts.push('충전 여부는 기기가 알려주지 않았습니다');
    return parts.join(' · ') || '배터리 상태';
};

// 토큰의 역할과 (보호자라면) 피보호자 목록. 이 화면은 주치의(센터 관리자)와
// 보호자 둘 다 연다 — 관리자 전용 NavigationBar 를 보호자에게는 그리지 않는다.
const readSessionFromToken = () => {
    try {
        const token = localStorage.getItem('token');
        if (!token) return { role: null, dependents: [] };
        const decoded = jwtDecode(token);
        return {
            role: decoded?.role || null,
            dependents: Array.isArray(decoded?.dependents) ? decoded.dependents : []
        };
    } catch (error) {
        return { role: null, dependents: [] };
    }
};

export default function VitalsDetail() {
    const { appUserId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [session] = useState(readSessionFromToken);
    const isGuardian = session.role === 'guardian';

    const [startDate, setStartDate] = useState(daysAgo(6));
    const [endDate, setEndDate] = useState(toDateInput(new Date()));
    const [activeMetric, setActiveMetric] = useState('heartRate');
    const [showTooltip, setShowTooltip] = useState(true);
    // 대상자 이름. id 만 보여주면 누구인지 알 수 없다.
    // 바이탈 API 는 이름을 주지 않으므로 대상자 조회에서 따로 받아 온다.
    // 이름: 보호자 대시보드에서 넘어오면 라우터 state 로, 새로고침한 보호자는 토큰의
    // dependents 로 채운다. 관리자는 아래에서 요약 API(/api/patients/:id/summary)로 받는다.
    const [patientName, setPatientName] = useState(() => {
        if (location.state?.patientName) return location.state.patientName;
        if (session.role === 'guardian') {
            const dependent = session.dependents.find(
                (d) => String(d.app_user_id) === String(appUserId)
            );
            return dependent?.name || null;
        }
        return null;
    });

    const [series, setSeries] = useState(null);
    const [daily, setDaily] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    // [최근] 버튼: 최근 데이터가 있는 날 하루로 기간을 맞춘다.
    const [isFindingLatest, setIsFindingLatest] = useState(false);
    const [latestNotice, setLatestNotice] = useState(null);
    // 눌린 프리셋 표시. 진입 시 기본은 최근 7일. 날짜를 직접 고치면 해제된다.
    const [activePreset, setActivePreset] = useState(7);
    // 수면 카드를 띄울 날짜. null 이면 팝업이 닫힌 상태다.
    const [sleepDate, setSleepDate] = useState(null);
    // 일일 요약에서 고른 날. 차트만 그 하루로 좁힌다.
    //
    // ⚠️ 조회 기간(startDate/endDate)을 그 날로 바꾸지 않는다. 바꾸면 표도 한 줄로
    //    줄어들어 방금 누른 표 자체가 사라지고, 다른 날을 보려면 기간을 되돌려야 한다.
    //    조회는 그대로 두고 이미 받아 온 포인트만 걸러 그린다 — 왕복도 늘지 않는다.
    const [focusedDate, setFocusedDate] = useState(null);

    const span = dayCount(startDate, endDate);
    // 서버 상한과 같은 값으로 미리 막는다. 400 을 받고 나서 알려주는 것보다 낫다.
    const rangeError = endDate < startDate
        ? '종료일이 시작일보다 빠릅니다.'
        : span > MAX_DAILY_DAYS
            ? `조회 기간은 최대 ${MAX_DAILY_DAYS}일입니다.`
            : null;
    const seriesTooLong = !rangeError && span > MAX_SERIES_DAYS;

    const load = useCallback(async () => {
        if (rangeError) return;

        setIsLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');
            // 시계열 상한(31일)을 넘는 기간에서는 일일 요약만 받는다.
            const [dailyResult, seriesResult] = await Promise.all([
                // 수면 세그먼트는 시계열과 같은 상한 안에서만 붙인다.
                // 밤마다 수십 행이라 1년치를 붙이면 응답이 무거워진다.
                fetchVitalsDaily({
                    token, appUserId, startDate, endDate,
                    includeSleepSegments: !seriesTooLong
                }),
                seriesTooLong
                    ? Promise.resolve(null)
                    : fetchVitalsSeries({ token, appUserId, startDate, endDate })
            ]);
            setDaily(dailyResult);
            setSeries(seriesResult);
        } catch (caught) {
            setError(describeVitalsError(caught));
            setDaily(null);
            setSeries(null);
        } finally {
            setIsLoading(false);
        }
    }, [appUserId, startDate, endDate, seriesTooLong, rangeError]);

    useEffect(() => { load(); }, [load]);

    // 기간이 바뀌면 고른 날은 버린다. 새 기간에 없는 날짜가 남아 있으면 차트가
    // 빈 채로 그려지고, 왜 비었는지 화면에 단서가 없다.
    useEffect(() => { setFocusedDate(null); }, [startDate, endDate]);

    // 관리자로 들어오면 이름을 요약 API 로 받는다. 예전에는 /api/health-data/:id 를
    // 불렀는데, 그 응답은 대상자의 측정 이력을 통째로 조인해 내려준다 — 이름 한 줄을
    // 얻자고 치를 값이 아니다.
    const summary = usePatientSummary(isGuardian ? null : appUserId);
    useEffect(() => {
        if (summary?.name) setPatientName(summary.name);
    }, [summary]);

    const metric = SERIES_METRICS.find((entry) => entry.key === activeMetric);
    // 기준선이 있는 지표는 셋이다. 셋 다 worker 가 계산해 저장한 값을 읽기만 한다 —
    // 정의를 화면에 복제하면 worker 만 바뀌었을 때 옛 기준선을 계속 그리게 된다.
    // 스트레스·SpO2 는 어디에도 정의가 없어 만들지 않는다 —
    // 지어낸 기준선은 없느니만 못하다.
    // 심박은 활동 시간대 기준선을 쓴다. 차트가 하루 전체 심박을 그리므로
    // 안정시(수면) 기준선을 얹으면 모든 점이 기준선 위에 뜬다.
    // 날짜별 기준선. worker 가 직전 7일로 계산하므로 값이 매일 움직인다.
    // 하루치를 계단식으로 유지해 그린다 — 그날 하루 동안은 같은 기준이다.
    const allBaselinePoints = (series?.baselines?.[activeMetric] || [])
        .map((entry) => ({ x: new Date(`${entry.measuredDate}T00:00:00+09:00`), y: entry.value }));

    // 표에서 고른 하루의 경계. 표의 measuredDate 는 KST 기준 날짜이고 시계열
    // measuredAt 은 절대시각이므로, 브라우저 시간대와 무관하게 KST 로 자른다 —
    // 기준선(baseline)이 이미 +09:00 을 명시해 쓰는 것과 같은 이유다.
    const focusStart = focusedDate ? new Date(`${focusedDate}T00:00:00+09:00`) : null;
    const focusEnd = focusStart ? new Date(focusStart.getTime() + 86400000) : null;
    const withinFocus = (point) => (
        !focusStart || (point.x >= focusStart && point.x < focusEnd)
    );

    // ⚠️ 하루만 볼 때는 그날 기준선 점이 하나뿐이라 선이 그려지지 않는다
    //    (pointRadius 0 이라 점도 안 보인다). 하루의 끝에 같은 값을 하나 더 놓아
    //    그날 내내 유지되는 기준선으로 긋는다.
    const focusedBaseline = allBaselinePoints.filter(withinFocus);
    const baselinePoints = focusStart && focusedBaseline.length === 1
        ? [focusedBaseline[0], { x: focusEnd, y: focusedBaseline[0].y }]
        : focusedBaseline;
    const hasBaseline = baselinePoints.some((point) => point.y !== null);
    const latestBaseline = [...baselinePoints].reverse().find((point) => point.y !== null)?.y ?? null;
    const points = (series?.series?.[activeMetric] || []).map((point) => ({
        x: new Date(point.measuredAt),
        y: point[metric.valueKey],
        deviceId: point.deviceId
    })).filter(withinFocus);

    // 고정 축을 벗어난 값이 있으면 Chart.js 가 축을 그만큼 넓힌다(suggestedMin/Max).
    // 그 기간만 그래프 높이가 달라지므로 각주로 알린다 — 모르고 보면 축이 같은 줄
    // 알고 다른 기간과 눈으로 비교하게 된다.
    const axisWidened = Boolean(metric.yRange) && points.some((point) => (
        point.y !== null && point.y !== undefined
        && (point.y < metric.yRange.min || point.y > metric.yRange.max)
    ));

    const chartData = {
        datasets: [
            {
                label: `${metric.label}${metric.unit ? ` (${metric.unit})` : ''}`,
                data: points,
                borderColor: METRIC_LINE_COLOR[activeMetric] || chartColor['6'],
                backgroundColor: METRIC_LINE_COLOR[activeMetric] || chartColor['6'],
                pointRadius: 2,
                // 기준선(2.5)보다 얇게 둔다 — 굵기 차이만으로도 둘이 구분된다.
                borderWidth: 1.2,
                // DashboardDetail 의 차트와 같은 곡률.
                tension: 0.4,
                // 기기가 바뀌면 점 사이를 잇지 않는다 — 이으면 없는 연속성이 생긴다.
                showLine: true,
                spanGaps: false
            },
            // 개인 기준선. 날짜마다 값이 달라 계단식으로 그린다.
            // 유효일수가 모자란 날은 y 가 null 이라 선이 끊긴다(spanGaps: false).
            // ⚠️ 색이 옅으면 데이터선에 묻힌다. 지표 색은 지표마다 달라지므로
            //    기준선은 어느 색과도 겹치지 않는 진한 중립색으로 고정하고,
            //    데이터선보다 굵게·긴 점선으로 그어 한눈에 구분되게 한다.
            //    데이터셋 순서가 뒤라 데이터선 위에 그려진다.
            ...(hasBaseline ? [{
                label: '기준선 (직전 7일)',
                data: baselinePoints,
                borderColor: textColor.default,
                borderDash: [10, 6],
                borderWidth: 2.5,
                pointRadius: 0,
                pointHitRadius: 0,
                stepped: 'after',
                spanGaps: false,
                fill: false
            }] : [])
        ]
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'nearest', intersect: false },
        scales: {
            x: {
                type: 'time',
                adapters: { date: { locale: ko } },
                // 하루만 볼 때는 축을 그 하루에 고정하고 시각 눈금을 찍는다.
                // 고정하지 않으면 데이터가 있는 시간대만큼만 축이 잡혀,
                // 오전에만 측정된 날과 온종일 측정된 날이 같은 폭으로 그려진다.
                ...(focusStart ? { min: focusStart, max: focusEnd } : {}),
                // 축에는 날짜만 찍는다. 시각은 툴팁에서 본다 —
                // 며칠치를 한 화면에 그리면 시분 라벨이 겹쳐 읽을 수 없다.
                time: focusStart
                    ? { unit: 'hour', tooltipFormat: 'MM-dd HH:mm', displayFormats: { hour: 'HH:mm' } }
                    : { unit: 'day', tooltipFormat: 'MM-dd HH:mm', displayFormats: { day: 'MM-dd' } },
                ticks: { maxRotation: 0, autoSkipPadding: 12, color: textColor['2'] },
                grid: { color: chartColor.grid },
                title: { display: false }
            },
            // 세로축은 지표별 고정 범위(vitalsApi.SERIES_METRICS.yRange)로 그린다.
            // 자동 범위는 그 사람 그 기간의 최소~최대에 맞춰 축을 늘렸다 줄였다 해서
            // 정상 범위 안의 흔들림도 산맥처럼 보이고, 사람끼리·기간끼리 비교가 안 된다.
            //
            // ⚠️ min/max 가 아니라 suggestedMin/suggestedMax 다. 범위를 벗어난 값이
            //    있으면 축이 그만큼 넓어진다 — 저산소나 고열처럼 가장 봐야 할 점이
            //    축 밖으로 잘려 사라지는 쪽이 훨씬 위험하다. 벗어난 기간에는
            //    아래 각주로 축이 넓어졌다고 알린다.
            y: {
                suggestedMin: metric.yRange?.min,
                suggestedMax: metric.yRange?.max,
                // 눈금 '글자'에 chart.axis 를 쓰지 않는다. 흰 배경에서 대비가 3.04:1 이라
                // 본문 기준(4.5:1)에 못 미친다 — 축 선 같은 비텍스트에만 쓴다.
                ticks: { color: textColor['2'] },
                grid: { color: chartColor.grid },
                title: { display: Boolean(metric.unit), text: metric.unit, color: textColor['2'] }
            }
        },
        plugins: {
            // 기준선이 있을 때만 범례를 띄운다. 지표 하나만 그릴 때는 필요 없다.
            legend: {
                display: hasBaseline,
                position: 'top',
                labels: { boxWidth: 28, boxHeight: 2, font: { size: 12 }, usePointStyle: false, color: textColor['2'] }
            },
            tooltip: {
                // 체크박스로 껐다 켠다. 점이 촘촘한 구간에서 툴팁이 계속 따라다니면
                // 그래프 모양을 보기 어렵다.
                enabled: showTooltip,
                callbacks: {
                    // 어느 기기에서 나온 점인지 보여준다. 링을 바꾸면 한 차트에
                    // 두 기기가 섞여 나온다.
                    afterLabel: (item) => `기기 ${item.raw.deviceId ?? '-'}`
                }
            }
        }
    };

    const days = daily?.days || [];
    const hasAnyDaily = days.some((day) => day.analysis || day.activity || day.sleep);

    // 세그먼트가 있는 날만 점수를 낼 수 있다. 표와 팝업이 같은 요약을 쓰도록
    // 한 번만 계산해 날짜로 찾아 쓴다 — 따로 계산하면 두 값이 갈라진다.
    const sleepByDate = new Map(
        days
            .filter((day) => day.sleep?.segments?.length > 0)
            .map((day) => [day.measuredDate, summarizeSleep(day.sleep.segments)])
            .filter(([, summary]) => summary !== null)
    );
    const openedSleepDay = sleepDate
        ? days.find((day) => day.measuredDate === sleepDate) || null
        : null;


    return (
        <div className={`vitals-page${isGuardian ? ' vitals-page--guardian' : ''}`}>
            {!isGuardian && <NavigationBar />}
            {/* DashboardDetail 과 같은 껍데기다 — 회색 바탕 위 sds-page 폭, 브레드크럼,
                sds-page-header, 탭 줄, 그 아래 흰 카드. 여섯 화면이 형제로 보이려면
                이 순서와 클래스가 같아야 한다.
                위 여백은 NavigationBar.css 의 .navigation-bar + * 가 준다. 보호자
                세션에는 그 바가 없어 선택자가 걸리지 않고 .sds-page 기본 여백만 남는다 —
                예전 .vitals-container--guardian 이 손으로 맞추던 값이 바로 그 값이다. */}
            <div className="sds-page detail-page">
                <div className="detail-page__crumb">
                    {/* 뒤로가기는 헤더 오른쪽이 아니라 브레드크럼 자리로 옮겼다(개요 화면과 같다).
                        보호자는 히스토리가 아니라 자기 관제센터로 돌아간다 — 예전 그대로다. */}
                    <button
                        className="sds-btn sds-btn--ghost sds-btn--sm"
                        onClick={() => (isGuardian ? navigate('/GuardianControlCenter') : navigate(-1))}
                    >
                        <ChevronLeft size={16} aria-hidden="true" />뒤로
                    </button>
                </div>

                <div className="sds-page-header">
                    <div>
                        {/* 제목은 대상자 이름이다. 어느 화면인지는 아래 탭 줄이 말한다 —
                            여섯 화면이 같은 사람을 보는 여섯 장이라는 것이 그렇게 읽힌다. */}
                        <h1 className="sds-page-header__title">
                            {patientName || `대상자 ${appUserId}`}
                            <small>스마트링 바이탈</small>
                        </h1>
                    </div>
                    <div className="sds-page-header__actions">
                        {/* 데이터가 비었을 때 "기록이 없는 것"과 "아직 동기화가 안 된 것"은
                            다르다. 기기→앱→Kafka→worker 를 거쳐 실시간이 아니다. */}
                        {daily?.lastSyncedAt && (
                            <span className="sds-badge sds-badge--none" title="기기에서 마지막으로 데이터를 받은 시각">
                                마지막 동기화 {formatSyncTime(daily.lastSyncedAt)}
                            </span>
                        )}
                        {/* 배터리는 기기가 알려줄 때만 적재된다(sync_sessions 의 소수 행).
                            정보가 없는 대상자에게는 아무것도 그리지 않는다 — '-' 를 그리면
                            방전된 것인지 안 온 것인지 구분이 안 된다. */}
                        {daily?.battery && (
                            <span
                                className={`sds-badge ${batteryBadgeClass(daily.battery)} vitals-page__battery`}
                                title={describeBattery(daily.battery)}
                            >
                                배터리 {daily.battery.percent}%
                                {daily.battery.charging && '(충전 중)'}
                            </span>
                        )}
                    </div>
                </div>

                {/* ⚠️ 보호자에게는 탭 줄을 그리지 않는다. 여섯 목적지 중 다섯(개요·측정기록·
                    케어플랜·설문·보고서)이 직원 화면이라, 보호자에게 보이면 막다른 길이거나
                    남의 대상자 데이터를 향한 문이 된다. hasSmartRing 은 이 화면에 도달했다는
                    것 자체가 참이다. */}
                {!isGuardian && <PatientTabs appUserId={appUserId} active="ring" hasSmartRing />}

                <div className="vitals-page__card vitals-page__range">
                    <label className="sds-field">
                        <span className="sds-label">시작일</span>
                        <input className="sds-input sds-input--sm" type="date" value={startDate} max={endDate}
                            onChange={(event) => { setActivePreset(null); setStartDate(event.target.value); }} />
                    </label>
                    <label className="sds-field">
                        <span className="sds-label">종료일</span>
                        <input className="sds-input sds-input--sm" type="date" value={endDate} min={startDate}
                            onChange={(event) => { setActivePreset(null); setEndDate(event.target.value); }} />
                    </label>
                    <div className="sds-seg vitals-page__presets">
                        {/* 최근 데이터가 있는 날 하루. 동기화가 늦거나 착용을 쉰 사람은
                            "최근 7일"이 비어 보이므로 데이터가 있는 마지막 날로 바로 간다. */}
                        <button
                            className={`sds-seg__item${activePreset === 'latest' ? ' is-active' : ''}`}
                            aria-current={activePreset === 'latest' ? 'true' : undefined}
                            disabled={isFindingLatest}
                            title="최근 데이터가 있는 날을 조회합니다"
                            onClick={async () => {
                                setIsFindingLatest(true);
                                setLatestNotice(null);
                                try {
                                    const latest = await fetchVitalsLatestDate({
                                        token: localStorage.getItem('token'), appUserId
                                    });
                                    if (latest) {
                                        setStartDate(latest);
                                        setEndDate(latest);
                                        setActivePreset('latest');
                                    } else {
                                        setLatestNotice('기록된 스마트링 데이터가 없습니다.');
                                    }
                                } catch (caught) {
                                    setLatestNotice(describeVitalsError(caught).message);
                                } finally {
                                    setIsFindingLatest(false);
                                }
                            }}
                        >
                            {isFindingLatest ? '찾는 중…' : '최근'}
                        </button>
                        {[7, 14, 30].map((count) => (
                            <button
                                key={count}
                                className={`sds-seg__item${activePreset === count ? ' is-active' : ''}`}
                                aria-current={activePreset === count ? 'true' : undefined}
                                onClick={() => {
                                    setLatestNotice(null);
                                    setActivePreset(count);
                                    setStartDate(daysAgo(count - 1));
                                    setEndDate(toDateInput(new Date()));
                                }}
                            >최근 {count}일</button>
                        ))}
                    </div>
                    <span className="vitals-page__range-span">{rangeError ? '' : `${span}일`}</span>
                </div>

                {rangeError && <div className="sds-notice sds-notice--error vitals-page__notice">{rangeError}</div>}
                {latestNotice && <div className="sds-notice vitals-page__notice">{latestNotice}</div>}

                {error && (
                    <div className="sds-notice sds-notice--error vitals-page__notice">
                        {error.message}
                        {error.code && <span className="vitals-page__notice-code">{error.code}</span>}
                    </div>
                )}

                {isLoading && <div className="sds-notice vitals-page__notice">불러오는 중…</div>}

                {!isLoading && !error && !rangeError && (
                    <>
                        <section className="vitals-page__card">
                            <div className="vitals-page__card-head">
                                <h2 className="sds-section-title">일일 요약</h2>
                            </div>
                            {!hasAnyDaily ? (
                                <div className="sds-notice">이 기간에 기록된 데이터가 없습니다.</div>
                            ) : (
                                <div className="sds-table-wrap vitals-page__table">
                                    <table className="sds-table">
                                        <thead>
                                            <tr>
                                                <th>날짜</th>
                                                <th className="sds-table__center">심박 평균</th>
                                                <th className="sds-table__center">HRV 평균</th>
                                                <th className="sds-table__center">수면</th>
                                                <th className="sds-table__center">수면 점수</th>
                                                <th className="sds-table__center">걸음</th>
                                                <th className="sds-table__center">회복 점수</th>
                                                <th className="sds-table__center">기기</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {days.map((day) => {
                                                // deviceCount 가 1 보다 크면 그날 여러 기기에 데이터가 있었고
                                                // 서버가 하나를 고른 것이다. "그날의 값"이 아니라
                                                // "선택된 기기의 값"이라는 뜻이라 표시해 준다.
                                                const source = day.analysis || day.activity || day.sleep;
                                                const shared = source && source.deviceCount > 1;
                                                // 세그먼트가 없으면 점수를 낼 수 없다.
                                                const sleepSummary = sleepByDate.get(day.measuredDate) || null;
                                                const isFocused = focusedDate === day.measuredDate;
                                                // 같은 행을 다시 누르면 해제해 전체 기간으로 돌아간다.
                                                const toggleFocus = () => setFocusedDate(isFocused ? null : day.measuredDate);
                                                // role="button" 은 붙이지 않는다 — tr 의 행 의미가 사라져
                                                // 표가 표로 읽히지 않는다. 키보드 접근은 tabIndex 와
                                                // Enter/Space 처리로만 준다.
                                                return (
                                                    <tr key={day.measuredDate}
                                                        className={`vitals-page__row${source ? '' : ' vitals-page__row--empty'}${isFocused ? ' is-selected' : ''}`}
                                                        onClick={toggleFocus}
                                                        onKeyDown={(event) => {
                                                            if (event.key === 'Enter' || event.key === ' ') {
                                                                event.preventDefault();
                                                                toggleFocus();
                                                            }
                                                        }}
                                                        tabIndex={0}
                                                        title={isFocused
                                                            ? '다시 누르면 전체 기간으로 돌아갑니다'
                                                            : '클릭하면 아래 차트가 이 날짜만 표시합니다'}>
                                                        <td>{day.measuredDate}</td>
                                                        <td className="sds-table__center">{formatNumber(day.analysis?.heartRateMean)}</td>
                                                        <td className="sds-table__center">{formatNumber(day.analysis?.hrvMeanMs)}</td>
                                                        {/* 분석값이 있으면 그것을, 없으면 수면 세션에서 채운다.
                                                            분석 행은 있는데 total_sleep_minutes 만 비어 있는 날이
                                                            있어, 그대로 두면 수면은 '-' 인데 점수는 나오는
                                                            앞뒤가 안 맞는 표가 된다. */}
                                                        <td className="sds-table__center">{day.analysis?.totalSleepMinutes != null
                                                            ? formatMinutes(day.analysis.totalSleepMinutes)
                                                            : formatSeconds(day.sleep?.totalSleepSeconds)}</td>
                                                        <td className="sds-table__center">
                                                            {sleepSummary ? (
                                                                <button
                                                                    className="sds-btn sds-btn--weak sds-btn--sm vitals-page__sleep-score"
                                                                    // 행 클릭(차트 날짜 좁히기)까지 함께 걸리면
                                                                    // 수면 상세를 여는 동시에 차트가 바뀐다.
                                                                    onClick={(event) => {
                                                                        event.stopPropagation();
                                                                        setSleepDate(day.measuredDate);
                                                                    }}
                                                                    title="클릭하면 수면 상세가 열립니다"
                                                                >
                                                                    {sleepSummary.score}
                                                                </button>
                                                            ) : '-'}
                                                        </td>
                                                        <td className="sds-table__center">{day.activity?.totalSteps ?? '-'}</td>
                                                        <td className="sds-table__center">{formatNumber(day.analysis?.recoveryScore, 0)}</td>
                                                        <td className="sds-table__center">
                                                            {source ? source.deviceId : '-'}
                                                            {shared && (
                                                                <span className="sds-badge sds-badge--caution vitals-page__device-warning"
                                                                    title={`이 날 ${source.deviceCount}개 기기에 데이터가 있어 하나를 골랐습니다`}>
                                                                    +{source.deviceCount - 1}
                                                                </span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </section>

                        <section className="vitals-page__card">
                            <div className="vitals-page__card-head">
                                <h2 className="sds-section-title">시계열</h2>
                                {/* 하루만 보고 있다는 것을 제목 옆에 적는다. 표는 여러 날을
                                    그대로 보여주므로, 적지 않으면 차트가 왜 하루치인지
                                    알 수 없다. */}
                                {focusedDate && (
                                    <span className="sds-badge sds-badge--info vitals-page__focus">
                                        {focusedDate}
                                        <button
                                            className="vitals-page__focus-clear"
                                            onClick={() => setFocusedDate(null)}
                                            title="전체 기간으로 돌아갑니다"
                                            aria-label="전체 기간으로 돌아가기"
                                        >
                                            <X size={16} aria-hidden="true" />
                                        </button>
                                    </span>
                                )}
                                <span className="sds-toolbar__spacer" />
                                {!seriesTooLong && (
                                    <label className="vitals-page__toggle">
                                        <input
                                            type="checkbox"
                                            checked={showTooltip}
                                            onChange={(event) => setShowTooltip(event.target.checked)}
                                        />
                                        툴팁 표시
                                        <span className="vitals-page__toggle-hint">
                                            (마우스를 올리면 시각·값·기기)
                                        </span>
                                    </label>
                                )}
                            </div>
                            {seriesTooLong ? (
                                <div className="sds-notice">
                                    시계열은 최대 {MAX_SERIES_DAYS}일까지 조회할 수 있습니다. 기간을 줄이면 표시됩니다.
                                </div>
                            ) : (
                                <>
                                    <div className="sds-seg sds-seg--lg vitals-page__metrics">
                                        {SERIES_METRICS.map((entry) => {
                                            // 하루를 고르면 그 하루의 포인트 수를 센다. 전체 기간
                                            // 개수를 그대로 두면 배지 숫자와 그려진 점 수가 어긋난다.
                                            const count = (series?.series?.[entry.key] || []).filter(
                                                (point) => withinFocus({ x: new Date(point.measuredAt) })
                                            ).length;
                                            return (
                                                <button
                                                    key={entry.key}
                                                    className={`sds-seg__item${activeMetric === entry.key ? ' is-active' : ''}`}
                                                    aria-current={activeMetric === entry.key ? 'true' : undefined}
                                                    onClick={() => setActiveMetric(entry.key)}
                                                >
                                                    {entry.label}
                                                    <span className="vitals-page__metric-count">{count}</span>
                                                </button>
                                            );
                                        })}
                                    </div>

                                    {series?.truncated && (
                                        <div className="sds-notice vitals-page__notice vitals-page__notice--warn">
                                            포인트가 너무 많아 일부만 표시합니다. 기간을 줄여 주세요.
                                        </div>
                                    )}

                                    {points.length === 0 ? (
                                        <div className="sds-notice">
                                            {focusedDate
                                                ? `${focusedDate} 에는 ${metric.label} 기록이 없습니다.`
                                                : `이 기간에 ${metric.label} 기록이 없습니다.`}
                                        </div>
                                    ) : (
                                        <div className="vitals-page__chart">
                                            <Line data={chartData} options={chartOptions} />
                                        </div>
                                    )}

                                    {/* 평상시에는 아무것도 적지 않는다. 축이 넓어진 때만 알린다 —
                                        그때만 이 그래프의 높이가 다른 기간과 달라지기 때문이다. */}
                                    {axisWidened && (
                                        <p className="vitals-page__footnote">
                                            일부 값이 표준 범위를 벗어나 세로축을 넓혔습니다.
                                        </p>
                                    )}

                                    {hasBaseline && (
                                        <p className="vitals-page__footnote">
                                            기준선은 날짜마다 그 날 직전 7일로 다시 계산합니다
                                            {latestBaseline !== null && ` (마지막 날 ${latestBaseline.toFixed(1)}${metric.unit ? ` ${metric.unit}` : ''})`}
                                            {activeMetric === 'heartRate'
                                                ? ' (활동 시간대 심박의 중앙값)'
                                                : activeMetric === 'temperature'
                                                    ? ' (일일 분석이 계산한 피부온 기준선)'
                                                    : ' (중앙값)'}.
                                            표본이 모자란 날은 표시하지 않습니다.
                                        </p>
                                    )}

                                    {/* SpO2 는 시간별 최솟값을 그린다. 테이블에 min/max 만 있어
                                        평균을 만들면 없는 데이터를 만드는 것이다. */}
                                    {activeMetric === 'spo2' && points.length > 0 && (
                                        <p className="vitals-page__footnote">
                                            SpO2 는 시간별 최솟값입니다. 원본에 평균이 없어 합성하지 않습니다.
                                        </p>
                                    )}
                                </>
                            )}
                        </section>
                    </>
                )}

                {/* 수면 상세는 표의 점수를 눌렀을 때만 띄운다. 날짜별 카드를
                    한 화면에 늘어놓으면 표에서 눈에 띈 날을 다시 찾아야 한다.
                    ⚠️ 공용 오버레이가 모달을 감싸므로 카드 안 클릭은 여기서 막는다.
                    예전에는 둘이 형제라 저절로 막혔다. */}
                {openedSleepDay && (
                    <div className="sds-modal-overlay" onClick={() => setSleepDate(null)}>
                        <div
                            className="sds-modal vitals-page__sleep-modal"
                            onClick={(event) => event.stopPropagation()}
                        >
                            <div className="sds-modal__head">
                                <h2 className="sds-modal__title">수면 상세</h2>
                                <button
                                    className="sds-btn sds-btn--ghost sds-btn--icon sds-btn--sm"
                                    onClick={() => setSleepDate(null)}
                                    aria-label="닫기"
                                >
                                    <X size={16} aria-hidden="true" />
                                </button>
                            </div>
                            <div className="sds-modal__body">
                                <SleepCard
                                    date={openedSleepDay.measuredDate}
                                    sleep={openedSleepDay.sleep}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
